import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Check,
  AlertCircle,
  ShieldCheck,
  ExternalLink,
  Sparkles,
  Cloud,
  Plus,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useBilling } from '../hooks/useBilling';
import { isDesktop } from '../lib/platform';
import DesktopBilling from '../components/desktop/DesktopBilling';
import { signIn, signUp, signInWithGoogle } from '../lib/auth';
import {
  startCheckout,
  openBillingPortal,
  tierLabel,
  formatBytes,
  isLifetimeTier,
  isWebsiteTier,
} from '../lib/billing';

// Plan card definitions. The page picks a subset to show based on the user's
// current tier (no point offering Pro to someone already on Pro).
const PLANS = {
  lite_lifetime: {
    product: 'lite_lifetime',
    price: '$49',
    cadence: 'one-time',
    name: 'Lite Lifetime',
    blurb: 'All core PDF tools + eSign (5/mo) + the desktop app — forever.',
  },
  pro_lifetime: {
    product: 'pro_lifetime',
    price: '$70',
    cadence: 'one-time',
    highlight: true,
    name: 'Pro Lifetime',
    blurb: 'Everything: full editor, OCR, batch, invoices, unlimited eSign — forever.',
  },
  cloud_ai: {
    product: 'cloud_ai',
    price: '$4.99',
    cadence: 'per month',
    name: 'Cloud & AI add-on',
    blurb: 'Cloud sync + all AI tools, on top of any lifetime license.',
  },
};

function Banner({ tone = 'info', children }) {
  const tones = {
    info: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    success: 'bg-green-50 border-green-200 text-green-900',
    error: 'bg-red-50 border-red-200 text-red-900',
    warn: 'bg-amber-50 border-amber-200 text-amber-900',
  };
  return (
    <div className={`border rounded-lg px-4 py-3 text-sm flex items-start gap-2 ${tones[tone]}`}>
      {tone === 'success' && <Check size={18} className="mt-0.5 shrink-0" />}
      {tone === 'error' && <AlertCircle size={18} className="mt-0.5 shrink-0" />}
      {tone === 'warn' && <AlertCircle size={18} className="mt-0.5 shrink-0" />}
      {tone === 'info' && <ShieldCheck size={18} className="mt-0.5 shrink-0" />}
      <div>{children}</div>
    </div>
  );
}

function PlanCard({ plan, busyProduct, onChoose, disabled }) {
  return (
    <div
      className={`rounded-xl border p-6 flex flex-col gap-4 ${
        plan.highlight
          ? 'border-indigo-500 shadow-md bg-gradient-to-br from-indigo-50 to-white'
          : 'border-gray-200 bg-white'
      }`}
    >
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-lg font-semibold">{plan.name}</h3>
          {plan.highlight && (
            <span className="text-xs font-semibold text-indigo-700 bg-indigo-100 rounded-full px-2 py-0.5">
              Most popular
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 mt-1">{plan.blurb}</p>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-3xl font-bold">{plan.price}</span>
        <span className="text-sm text-gray-500">{plan.cadence}</span>
      </div>
      <button
        type="button"
        onClick={() => onChoose(plan.product)}
        disabled={disabled || busyProduct === plan.product}
        className={`mt-2 rounded-lg py-2.5 text-sm font-semibold transition ${
          plan.highlight
            ? 'bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-indigo-300'
            : 'bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-400'
        } disabled:cursor-not-allowed`}
      >
        {busyProduct === plan.product ? 'Redirecting to Stripe…' : (plan.ctaLabel || `Choose ${plan.name}`)}
      </button>
    </div>
  );
}

function CapabilityStat({ icon, label, value }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-base font-semibold">{value}</div>
    </div>
  );
}

function CloudUsageBar({ used, limit }) {
  if (!limit) return null;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  return (
    <div className="mt-4">
      <div className="flex justify-between text-xs text-gray-600 mb-1">
        <span>{formatBytes(used)} of {formatBytes(limit)} used</span>
        <span>{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
        <div
          className={`h-full rounded-full ${pct > 90 ? 'bg-red-500' : 'bg-indigo-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function formatPeriodEnd(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return null;
  }
}

// Inline auth lives on the Billing page so an unauthenticated visitor from
// /pricing can sign up or sign in without losing the plan context — the form
// sits above the same plan cards they'll see after auth.
function translateFirebaseError(err, opts = {}) {
  const code = err?.code || '';
  const { onSwitchToSignIn, onSwitchToSignUp, prefillEmail } = opts;

  if (code.includes('email-already-in-use')) {
    return (
      <span>
        There&rsquo;s already a Scrivar account with that email.{' '}
        {onSwitchToSignIn && (
          <button
            type="button"
            className="underline font-medium hover:text-red-800"
            onClick={() => onSwitchToSignIn(prefillEmail)}
          >
            Sign in instead?
          </button>
        )}
      </span>
    );
  }
  if (code.includes('invalid-email')) return "That doesn't look like a valid email address.";
  if (code.includes('weak-password')) return 'Password needs to be at least 6 characters.';
  if (code.includes('wrong-password') || code.includes('invalid-credential')) {
    return 'Wrong email or password.';
  }
  if (code.includes('user-not-found')) {
    return (
      <span>
        No Scrivar account with that email.{' '}
        {onSwitchToSignUp && (
          <button
            type="button"
            className="underline font-medium hover:text-red-800"
            onClick={onSwitchToSignUp}
          >
            Create one?
          </button>
        )}
      </span>
    );
  }
  if (code.includes('too-many-requests')) {
    return 'Too many sign-in attempts. Wait a few minutes and try again.';
  }
  if (code.includes('popup-closed-by-user') || code.includes('cancelled-popup-request')) {
    return 'Google sign-in was cancelled.';
  }
  if (code.includes('network-request-failed')) {
    return "Can't reach Scrivar right now. Check your internet connection and try again.";
  }
  return err?.message || 'Something went wrong. Please try again.';
}

function GoogleIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.56c2.08-1.92 3.28-4.74 3.28-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.77c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.43.34-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}

function InlineAuth({ selectedPlan }) {
  // Default to signup — most visitors here came from /pricing and don't have
  // an account yet. Returning users can flip to the Sign in tab.
  const [tab, setTab] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const switchToSignIn = (prefill) => {
    setTab('signin');
    if (prefill) setEmail(prefill);
    setError(null);
  };

  const switchToSignUp = () => {
    setTab('signup');
    setError(null);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signIn(email.trim(), password);
      // No navigation — useAuth fires onAuthStateChanged, the !user guard
      // above falls through, and the real Billing page renders.
    } catch (err) {
      setError(translateFirebaseError(err, { onSwitchToSignUp: switchToSignUp }));
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await signUp(email.trim(), password);
      // signUp() also creates the session; Firebase auto-signs in the user,
      // so useAuth updates and the Billing page takes over. The verification
      // email was dispatched inside signUp — no blocking screen here.
    } catch (err) {
      setError(translateFirebaseError(err, { onSwitchToSignIn: switchToSignIn, prefillEmail: email }));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError(null);
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(translateFirebaseError(err));
    } finally {
      setLoading(false);
    }
  };

  const heading = tab === 'signup'
    ? 'Create your account to continue'
    : 'Sign in to continue';

  return (
    <div className="max-w-md mx-auto px-4 py-10 sm:py-12">
      <div className="mb-5 flex items-center gap-2">
        <Link to="/pricing" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft size={16} className="mr-1" /> Back to pricing
        </Link>
      </div>

      <h1 className="text-2xl sm:text-3xl font-semibold text-center tracking-tight">{heading}</h1>

      {selectedPlan && (
        <div className="mt-5 rounded-lg border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-4 text-center">
          <div className="text-xs uppercase tracking-wide text-indigo-700 font-semibold">You selected</div>
          <div className="mt-1 text-base font-semibold text-gray-900">
            {selectedPlan.name} — {selectedPlan.price} {selectedPlan.cadence}
          </div>
        </div>
      )}

      <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-100">
          {[
            { id: 'signup', label: 'Create account' },
            { id: 'signin', label: 'Sign in' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setTab(t.id);
                setError(null);
              }}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                tab === t.id
                  ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/40'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          <button
            type="button"
            onClick={handleGoogle}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 sm:py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 min-h-[44px]"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" aria-hidden="true" />
            ) : (
              <GoogleIcon />
            )}
            {tab === 'signin' ? 'Sign in with Google' : 'Sign up with Google'}
          </button>

          <div className="flex items-center gap-3 my-4 text-xs text-gray-400">
            <div className="h-px flex-1 bg-gray-100" />
            <span>or</span>
            <div className="h-px flex-1 bg-gray-100" />
          </div>

          <form
            onSubmit={tab === 'signin' ? handleSignIn : handleSignUp}
            className="space-y-3"
          >
            <div>
              <label htmlFor="inline-auth-email" className="sr-only">Email address</label>
              <div className="flex items-center gap-2 px-3 py-3 sm:py-2.5 border border-gray-200 rounded-lg focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20">
                <Mail className="w-4 h-4 text-gray-400 shrink-0" aria-hidden="true" />
                <input
                  id="inline-auth-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full outline-none text-base sm:text-sm bg-transparent"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="inline-auth-password" className="sr-only">Password</label>
              <div className="flex items-center gap-2 px-3 py-3 sm:py-2.5 border border-gray-200 rounded-lg focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20">
                <Lock className="w-4 h-4 text-gray-400 shrink-0" aria-hidden="true" />
                <input
                  id="inline-auth-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete={tab === 'signin' ? 'current-password' : 'new-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full outline-none text-base sm:text-sm bg-transparent"
                  disabled={loading}
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="-mr-2 -my-3 sm:-my-2.5 px-3 py-3 text-gray-400 hover:text-gray-600 rounded-md inline-flex items-center justify-center min-w-[44px] min-h-[44px]"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={0}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {tab === 'signup' && (
              <div>
                <label htmlFor="inline-auth-confirm" className="sr-only">Confirm password</label>
                <div className="flex items-center gap-2 px-3 py-3 sm:py-2.5 border border-gray-200 rounded-lg focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20">
                  <Lock className="w-4 h-4 text-gray-400 shrink-0" aria-hidden="true" />
                  <input
                    id="inline-auth-confirm"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm password"
                    className="w-full outline-none text-base sm:text-sm bg-transparent"
                    disabled={loading}
                    minLength={6}
                  />
                </div>
              </div>
            )}

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3 sm:py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2 min-h-[44px]"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  {tab === 'signin' ? 'Signing in\u2026' : 'Creating account\u2026'}
                </>
              ) : (
                <>
                  {tab === 'signin' ? 'Sign in' : 'Continue to checkout'}
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      <p className="mt-4 text-center text-xs text-gray-500">
        Have a Scrivar registration code?{' '}
        <Link to="/activate" className="text-gray-600 hover:text-gray-800 underline">
          Activate it here
        </Link>
        .
      </p>
    </div>
  );
}

function WebBilling() {
  const { user, loading: authLoading, account } = useAuth();
  const {
    tier,
    capabilities,
    subscription,
    addonSubscription,
    hasLifetime,
    cloudUsage,
    stripeEnabled,
    refresh,
    refreshing: billingRefreshing,
    error: billingError,
  } = useBilling();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const checkoutFlag = params.get('checkout');

  // On return from Stripe Checkout, re-sync billing state so the UI reflects
  // the new tier immediately instead of waiting for the next /account round-trip.
  useEffect(() => {
    if (checkoutFlag === 'success') {
      refresh().catch(() => {});
    }
  }, [checkoutFlag, refresh]);

  // Clear the query param after 8 seconds so the banner doesn't linger.
  useEffect(() => {
    if (!checkoutFlag) return undefined;
    const t = setTimeout(() => {
      params.delete('checkout');
      params.delete('session');
      setParams(params, { replace: true });
    }, 8000);
    return () => clearTimeout(t);
  }, [checkoutFlag, params, setParams]);

  const chooseProduct = async (product) => {
    if (!stripeEnabled) return;
    setBusy(product);
    setError(null);
    try {
      // All purchases use Checkout (lifetime one-time + cloud_ai add-on).
      // The Stripe portal is only used to manage an existing cloud_ai add-on
      // (cancel / update payment) — see manageBilling below.
      await startCheckout(product);
    } catch (err) {
      setError(err.message || 'Checkout failed.');
      setBusy(null);
    }
  };

  const manageBilling = async () => {
    setBusy('portal');
    setError(null);
    try {
      await openBillingPortal();
    } catch (err) {
      setError(err.message || 'Could not open Stripe portal.');
      setBusy(null);
    }
  };

  // The summary of the user's current plan, derived from tier + sub state.
  const currentPlan = useMemo(
    () => tierLabel({ tier, addonSubscription }),
    [tier, addonSubscription]
  );
  const periodEnd = formatPeriodEnd(subscription?.currentPeriodEnd);
  const addonEnd = formatPeriodEnd(addonSubscription?.currentPeriodEnd);

  // Build the plan card list based on the user's current tier + addon state.
  let plansToShow = [];
  let sectionHeading = 'Choose a plan';

  if (tier === 'free') {
    plansToShow = [
      { ...PLANS.lite_lifetime, ctaLabel: 'Buy Lite — $49' },
      { ...PLANS.pro_lifetime, ctaLabel: 'Buy Pro — $70' },
    ];
    sectionHeading = 'Choose a plan';
  } else if (isLifetimeTier(tier) && !addonSubscription) {
    plansToShow = [{ ...PLANS.cloud_ai, ctaLabel: 'Add Cloud & AI — $4.99/mo' }];
    if (tier === 'lifetime_lite') {
      plansToShow.push({ ...PLANS.pro_lifetime, ctaLabel: 'Upgrade to Pro — $70' });
    }
    sectionHeading = 'Add cloud & AI';
  }
  // isLifetimeTier(tier) && addonSubscription → no purchase cards (fully covered).

  if (authLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 flex items-center justify-center text-gray-500">
        <Loader2 className="animate-spin mr-2" size={18} />
        Loading billing…
      </div>
    );
  }

  if (!user) {
    const planParam = params.get('plan');
    const selectedPlan =
      planParam === 'pro_lifetime' ? PLANS.pro_lifetime
      : planParam === 'lite_lifetime' ? PLANS.lite_lifetime
      : planParam === 'cloud_ai' ? PLANS.cloud_ai
      : null;
    return <InlineAuth selectedPlan={selectedPlan} />;
  }

  const showCloudUsage = (capabilities?.cloud_bytes || 0) > 0;
  const showAiBadge = Boolean(capabilities?.ai_tools);

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="mb-6 flex items-center gap-2">
        <Link to="/account" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft size={16} className="mr-1" /> Back to account
        </Link>
      </div>

      <h1 className="text-3xl font-bold tracking-tight">Billing & plan</h1>
      <p className="text-gray-600 mt-1">Manage your Scrivar subscription and explore upgrades.</p>

      <div className="mt-6 space-y-3">
        {checkoutFlag === 'success' && (
          <Banner tone="success">
            Thanks for subscribing! Your plan is being finalized. If features don't unlock
            within a minute, refresh this page.
          </Banner>
        )}
        {checkoutFlag === 'canceled' && (
          <Banner tone="warn">Checkout canceled — no changes to your plan.</Banner>
        )}
        {error && <Banner tone="error">{error}</Banner>}
        {/*
          Bug 3: useBilling.refresh() throws when /billing/status fails (CORS,
          network, 5xx) but the calling effect silently catches. Without this
          banner the page would render Free-tier defaults with no clue why the
          real plan didn't load — exactly what the lifetime/Pro tester saw
          ("plan not detected"). Show the error AND a manual retry so the user
          can recover without reloading the entire app.
        */}
        {billingError && !error && (
          <Banner tone="warn">
            <div>
              Couldn't load your latest billing details
              {billingError?.message ? `: ${billingError.message}` : '.'} Showing
              what we know from your account. Your plan may not reflect a recent
              change until this succeeds.{' '}
              <button
                type="button"
                onClick={() => { refresh().catch(() => {}); }}
                disabled={billingRefreshing}
                className="underline font-medium hover:no-underline disabled:opacity-50"
              >
                {billingRefreshing ? 'Retrying…' : 'Try again'}
              </button>
            </div>
          </Banner>
        )}
        {/*
          Signed in via Firebase but useAuth's /account fetch hasn't populated
          (or failed too). Without an account row useBilling can't even start —
          accountId is undefined, the refresh effect never fires, and tier
          defaults to 'free'. Make that state visible instead of presenting a
          confidently-wrong Free plan.
        */}
        {user && !account && !authLoading && (
          <Banner tone="warn">
            We couldn't load your account, so this page is showing the free-tier
            defaults. Open <a href="/account" className="underline font-medium hover:no-underline">My Account</a>{' '}
            to retry, or contact support if the problem persists.
          </Banner>
        )}
        {!stripeEnabled && (
          <Banner tone="info">
            Subscriptions are launching soon. Enter your email on the pricing page and
            we'll notify you when they go live.
          </Banner>
        )}
      </div>

      {/* Current plan card */}
      <div className="mt-8 rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
              Current plan
            </div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <h2 className="text-2xl font-semibold">{currentPlan.label}</h2>
              {showAiBadge && <Sparkles size={18} className="text-indigo-600" />}
              {hasLifetime && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-900 bg-amber-100 rounded-full px-2 py-0.5">
                  <ShieldCheck size={12} /> Lifetime license
                </span>
              )}
              {addonSubscription && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-900 bg-indigo-100 rounded-full px-2 py-0.5">
                  <Plus size={12} /> Cloud & AI Add-on
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-1">{currentPlan.tagline}</p>
            {subscription && periodEnd && (
              <p className="text-sm text-gray-500 mt-2">
                {subscription.cancelAtPeriodEnd
                  ? `Access ends ${periodEnd}.`
                  : `Renews ${periodEnd}.`}
              </p>
            )}
            {addonSubscription && addonEnd && (
              <p className="text-sm text-gray-500 mt-1">
                Cloud & AI add-on {addonSubscription.cancelAtPeriodEnd
                  ? `ends ${addonEnd}.`
                  : `renews ${addonEnd}.`}
              </p>
            )}
            {tier === 'free' && (
              <p className="text-sm text-gray-500 mt-2">
                Free tier includes every core PDF tool with a 3-task-per-day limit. No watermarks.
              </p>
            )}
            {isLifetimeTier(tier) && !addonSubscription && (
              <p className="text-sm text-gray-500 mt-2">
                Your lifetime license includes all PDF tools and eSign — forever, no monthly cost.
              </p>
            )}
          </div>
          {(subscription || addonSubscription) && stripeEnabled && (
            <button
              type="button"
              onClick={manageBilling}
              disabled={busy === 'portal'}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-60"
            >
              {busy === 'portal'
                ? 'Opening…'
                : addonSubscription && !isWebsiteTier(tier)
                  ? 'Manage add-on'
                  : 'Manage subscription'}
              <ExternalLink size={14} />
            </button>
          )}
        </div>

        {capabilities && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <CapabilityStat
              icon={<Cloud size={16} className="text-indigo-600" />}
              label="Cloud storage"
              value={
                capabilities.cloud_bytes
                  ? formatBytes(capabilities.cloud_bytes)
                  : 'Not included'
              }
            />
            <CapabilityStat
              icon={<Sparkles size={16} className="text-indigo-600" />}
              label="AI tools"
              value={capabilities.ai_tools ? 'All 5 tools' : 'Not included'}
            />
            <CapabilityStat
              icon={<ShieldCheck size={16} className="text-indigo-600" />}
              label="Signature requests"
              value={
                capabilities.esign_request === null
                  ? 'Unlimited'
                  : capabilities.esign_request
                  ? `${capabilities.esign_request} / month`
                  : 'Not included'
              }
            />
          </div>
        )}

        {showCloudUsage && cloudUsage && (
          <CloudUsageBar used={cloudUsage.used_bytes || 0} limit={cloudUsage.limit_bytes || 0} />
        )}
      </div>

      {/* Plan cards — only shown when there's a meaningful alternative path
          AND Stripe is wired up. Showing change-plan cards with disabled
          buttons under a "Subscriptions are launching soon" banner is a
          confusing two-message contradiction (the banner says "not yet" but
          the card shape says "right here"). Hide the whole section when
          !stripeEnabled — the banner alone tells the story. */}
      {plansToShow.length > 0 && stripeEnabled && (
        <>
          <h2 className="text-xl font-semibold mt-10">{sectionHeading}</h2>
          {tier === 'free' && (
            <p className="text-sm text-gray-600 mt-1">
              One-time purchase — no subscription required.{' '}
              <Link to="/activate" className="underline hover:text-gray-800">
                Have an Amazon registration code? Activate it here.
              </Link>
            </p>
          )}
          {isLifetimeTier(tier) && !addonSubscription && (
            <p className="text-sm text-gray-600 mt-1">
              Add cloud sync and AI tools on top of your lifetime license. Your base license
              never expires — cancel the add-on anytime and you keep everything you bought.
            </p>
          )}
          <div className={`mt-4 grid grid-cols-1 ${plansToShow.length > 1 ? 'md:grid-cols-2' : 'md:max-w-md'} gap-5`}>
            {plansToShow.map((plan) => (
              <PlanCard
                key={plan.product}
                plan={plan}
                busyProduct={busy}
                onChoose={chooseProduct}
                disabled={!stripeEnabled}
              />
            ))}
          </div>
        </>
      )}

      {tier === 'lifetime_pro' && addonSubscription && (
        <div className="mt-10 rounded-xl border border-gray-200 bg-gray-50 p-5">
          <h3 className="text-base font-semibold">You're fully covered.</h3>
          <p className="text-sm text-gray-600 mt-1">
            Pro Lifetime + Cloud & AI is the highest combination Scrivar offers — every
            feature is unlocked, with cloud sync and AI on top of your lifetime license.
            To cancel the Cloud & AI add-on or update payment, use "Manage add-on" above.
          </p>
        </div>
      )}

      <p className="text-xs text-gray-500 mt-6">
        Secure payment handled by Stripe. Lifetime licenses are one-time. The Cloud &amp; AI
        add-on is billed monthly and can be canceled anytime.
      </p>
    </div>
  );
}

export default function Billing() {
  if (isDesktop()) return <DesktopBilling />;
  return <WebBilling />;
}
